export class LagrangeInterpolation {
  constructor(points, isEvenlySpaced = false) {
    this.points = points;
    this.isEvenlySpaced = isEvenlySpaced;
  }

  findOrdinateFor(x) {
    let res = 0;

    for (let i = 0; i < this.points.length; i++) {
      let numerator = 1;

      for (let j = 0; j < this.points.length; j++) {
        if (j != i) numerator *= x - (this.isEvenlySpaced ? j : this.isEvenlySpaced ? j : this.points[j].x);
      }

      let denominator = 1;

      for (let j = 0; j < this.points.length; j++) {
        if (j != i) denominator *= (this.isEvenlySpaced ? i : this.isEvenlySpaced ? i : this.points[i].x) - (this.isEvenlySpaced ? j : this.isEvenlySpaced ? j : this.points[j].x);
      }

      res += numerator / denominator * this.points[i].y;
    }

    return {
      x,
      y: Math.floor(res * 1000) / 1000
    };
  }

}
export class MonotoneCubicInterpolation {
  constructor(data, isEvenlySpaced = false, samplesNumber) {
    this.points = data;
    this.isEvenlySpaced = isEvenlySpaced;
    this.samplesNumber = samplesNumber;

    if (this.isEvenlySpaced) {
      this.points = this.points.map((v, i) => ({
        x: i,
        y: v.y
      }));
    }

    this.result = this.interpolate();
  }

  interpolate() {
    const n = this.points.length;
    let slopes = new Array(n - 1);
    let tangents = new Array(n); // Step 1: Compute slopes of secant lines between successive points

    for (let i = 0; i < n - 1; i++) {
      slopes[i] = (this.points[i + 1].y - this.points[i].y) / (this.points[i + 1].x - this.points[i].x);
    } // Step 2: Initialize tangents as average of slopes


    tangents[0] = slopes[0];

    for (let i = 1; i < n - 1; i++) {
      if (slopes[i - 1] * slopes[i] > 0) {
        tangents[i] = (slopes[i - 1] + slopes[i]) / 2;
      } else {
        tangents[i] = 0;
      }
    }

    tangents[n - 1] = slopes[n - 2]; // Step 3: Adjust tangents using Fritsch-Carlson method

    for (let i = 0; i < n - 1; i++) {
      if (slopes[i] === 0) {
        // Prevent division by zero
        tangents[i] = 0;
        tangents[i + 1] = 0;
      } else {
        let alpha = tangents[i] / slopes[i];
        let beta = tangents[i + 1] / slopes[i];
        let tau = Math.sqrt(alpha * alpha + beta * beta);

        if (tau > 3) {
          tau = 3 / tau;
          tangents[i] = tau * alpha * slopes[i];
          tangents[i + 1] = tau * beta * slopes[i];
        }
      }
    } // Step 4: Interpolate using cubic Hermite spline


    let interpolatedValues = [];
    let step = (this.points[n - 1].x - this.points[0].x) / (this.samplesNumber - 1);

    for (let i = 0; i < this.samplesNumber; i++) {
      let x = this.points[0].x + i * step;
      interpolatedValues.push({
        x,
        y: this.evaluateSpline(x, tangents)
      });
    }

    return interpolatedValues;
  }

  evaluateSpline(x, tangents) {
    // Find appropriate segment
    let i = 0;

    while (i < this.points.length - 2 && x > this.points[i + 1].x) {
      i++;
    } // Compute Hermite basis functions and return interpolated value


    let h00 = (1 + 2 * (x - this.points[i].x) / (this.points[i + 1].x - this.points[i].x)) * Math.pow((x - this.points[i + 1].x) / (this.points[i].x - this.points[i + 1].x), 2);
    let h10 = (x - this.points[i].x) * Math.pow((x - this.points[i + 1].x) / (this.points[i].x - this.points[i + 1].x), 2);
    let h01 = (1 + 2 * (x - this.points[i + 1].x) / (this.points[i].x - this.points[i + 1].x)) * Math.pow((x - this.points[i].x) / (this.points[i + 1].x - this.points[i].x), 2);
    let h11 = (x - this.points[i + 1].x) * Math.pow((x - this.points[i].x) / (this.points[i + 1].x - this.points[i].x), 2);
    return h00 * this.points[i].y + h10 * tangents[i] + h01 * this.points[i + 1].y + h11 * tangents[i + 1];
  }

}